import { useCallback, useEffect, useState } from "react";

import logger from "~/helpers/logger";
import fetchTenantIdByPortalHost from "~portal/helpers/customDomainTenantApi";

export type CustomDomainTenantResolution =
	| { status: "loading" }
	| { status: "notFound" }
	| { status: "error"; retry: () => void }
	| { status: "resolved"; tenantId: string };

const useCustomDomainTenantResolution = (host: string): CustomDomainTenantResolution => {
	const [resolution, setResolution] = useState<CustomDomainTenantResolution>({ status: "loading" });
	const [attempt, setAttempt] = useState(0);

	const retry = useCallback(() => setAttempt((previousAttempt) => previousAttempt + 1), []);

	useEffect(() => {
		let isCurrent = true;
		setResolution({ status: "loading" });
		(async () => {
			try {
				const tenantId = await fetchTenantIdByPortalHost(host);
				if (!isCurrent) return;
				setResolution(tenantId ? { status: "resolved", tenantId } : { status: "notFound" });
			} catch (resolveErrorCause) {
				if (!isCurrent) return;
				logger.error("Failed to resolve custom portal domain host", resolveErrorCause);
				setResolution({ status: "error", retry });
			}
		})();
		return () => {
			isCurrent = false;
		};
	}, [host, attempt, retry]);

	return resolution;
};

export default useCustomDomainTenantResolution;
